import { Component, EventEmitter, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { CustomFieldsService } from '../../services/custom-fields.service';
import { CustomField, CustomFieldDataType, CustomFieldOption, CustomFieldValue } from '../../models/custom-field.model';

/**
 * Inputs for the active HCO/HCP custom fields, placed inside an existing <form #f="ngForm">.
 * The inputs join the parent form (viewProviders below), so `required` blocks the parent's
 * save exactly like its own fields. Renders nothing while the feature setting is off.
 *
 * Bind with [(values)]="dto.customFields": every active field is sent back, an empty value clears it.
 */
@Component({
  selector: 'crm-custom-field-inputs',
  templateUrl: './custom-field-inputs.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class CustomFieldInputsComponent implements OnInit, OnChanges {
  @Input() entity: 'hco' | 'hcp';
  @Input() values: CustomFieldValue[];
  @Output() valuesChange = new EventEmitter<CustomFieldValue[]>();
  @Input() disabled = false;
  @Input() columnClass = 'col-sm-6';

  fields: CustomField[] = [];
  model: { [fieldId: number]: any } = {};
  /** Dropdown items of each Multiple choice field: active choices + any inactive one already selected. */
  multiOptions: { [fieldId: number]: { label: string, value: number }[] } = {};
  types = CustomFieldDataType;

  constructor(private readonly customFieldsService: CustomFieldsService, @Optional() public form: NgForm) {
  }

  ngOnInit() {
    this.customFieldsService.getActive(this.entity).subscribe(fields => {
      this.fields = fields || [];
      this.readValues();
      if (this.fields.length > 0) {
        // The active fields can come from cache synchronously, i.e. during the parent's change
        // detection; updating the parent's binding right now throws ExpressionChangedAfterItHasBeenChecked.
        setTimeout(() => this.emit());
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // A new DTO was loaded into the parent (e.g. after getById) — refresh the inputs.
    if (changes.values && !changes.values.firstChange && changes.values.currentValue !== this.lastEmitted) {
      this.readValues();
    }
  }

  private lastEmitted: CustomFieldValue[];

  private readValues() {
    const model = {};
    for (const field of this.fields) {
      const stored = (this.values || []).find(v => v.fieldId === field.id);
      const raw = stored && stored.value != null ? stored.value : '';
      model[field.id] = field.dataType === CustomFieldDataType.MultipleChoice
        ? raw.split(',').filter(x => x).map(x => +x)
        : raw;
    }
    this.model = model;
    const multiOptions = {};
    for (const field of this.fields.filter(f => f.dataType === CustomFieldDataType.MultipleChoice)) {
      multiOptions[field.id] = this.choicesOf(field).map(o => ({ label: o.value, value: o.id }));
    }
    this.multiOptions = multiOptions;
  }

  emit() {
    const values: CustomFieldValue[] = this.fields.map(field => ({ fieldId: field.id, value: this.rawOf(field) }));
    this.lastEmitted = values;
    this.valuesChange.emit(values);
  }

  private rawOf(field: CustomField): string {
    const v = this.model[field.id];
    if (field.dataType === CustomFieldDataType.MultipleChoice) {
      return (v || []).slice().sort((a, b) => a - b).join(',');
    }
    if (field.dataType === CustomFieldDataType.Boolean) {
      return v === 'True' ? 'True' : 'False';
    }
    return v == null ? '' : ('' + v).trim();
  }

  /** Active choices, plus an inactive one the record already has (so it still shows). */
  choicesOf(field: CustomField): CustomFieldOption[] {
    const current = this.model[field.id];
    const selected: number[] = Array.isArray(current) ? current : (current ? [+current] : []);
    return field.options.filter(o => o.isActive || selected.indexOf(o.id) >= 0);
  }



  setBoolean(field: CustomField, checked: boolean) {
    this.model[field.id] = checked ? 'True' : 'False';
    this.emit();
  }


  showError(field: CustomField): boolean {
    const control = this.form && this.form.controls['cf_' + field.id];
    return !!control && control.invalid && (control.dirty || control.touched || this.form.submitted);
  }
}
